import { useIntl } from 'react-intl';
import { StatusBadge, type StatusBadgeTone } from '@immo/ui-kit';
import type { FleetAccount, FleetAccountAuth } from '@immo/api-contract';
import { useTranslations } from '@/i18n/use-translations';

/** Red for the two states that take a lane out of service; the rest are facts, not alarms. */
const AUTH_TONE: Record<FleetAccountAuth, StatusBadgeTone> = {
  ok: 'success',
  'logged-out': 'danger',
  'usage-limit': 'danger',
  unknown: 'neutral',
};

function percent(value: number): string {
  return `${Math.round(value * 100)}%`;
}

/**
 * The engine accounts the host's lanes run on, as its last tick reported them
 * (fleet-agent 0.18.0): who is logged in, from which config dir, and how much
 * of the plan is left. One row per account — a host runs one or two, not one
 * per lane.
 */
export function MachineAccounts({ accounts }: { readonly accounts: ReadonlyArray<FleetAccount> }) {
  const t = useTranslations('pages.admin.fleet.accounts');

  return (
    <div className="mt-3">
      <span className="text-sm text-text-tertiary">{t('title')}</span>
      <ul className="mt-1.5 flex flex-col gap-1.5">
        {accounts.map((account) => (
          <li
            key={`${account.engine}:${account.configDir ?? ''}`}
            className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1"
          >
            <span className="min-w-0 break-words font-mono text-base-sm text-text-primary">
              {account.email ?? t('unknown-email')}
            </span>
            <span className="min-w-0 break-words font-mono text-base-sm text-text-secondary">
              {account.configDir ?? t('default-dir')}
            </span>
            <StatusBadge label={t(`auth.${account.auth}`)} tone={AUTH_TONE[account.auth]} dot />
            <QuotaLine account={account} />
            <AccountScope account={account} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function QuotaLine({ account }: { readonly account: FleetAccount }) {
  const t = useTranslations('pages.admin.fleet.accounts');
  const quota = account.quota;

  if (quota === null) return null;

  const windows = [
    ...(quota.week !== null ? [t('week', { percent: percent(quota.week) })] : []),
    ...(quota.session !== null ? [t('session', { percent: percent(quota.session) })] : []),
  ];

  if (windows.length === 0 && !quota.limited) return null;

  return (
    <span className="whitespace-nowrap font-mono text-sm text-text-tertiary">
      {windows.join(' / ')}
      {quota.limited && <span className="text-danger-600"> · {t('rate-limited')}</span>}
    </span>
  );
}

/** Which lanes the account serves, on which engine, and when its quota was last read. */
function AccountScope({ account }: { readonly account: FleetAccount }) {
  const t = useTranslations('pages.admin.fleet.accounts');
  const intl = useIntl();
  const parts = [
    ...(account.lanes.length > 0 ? [t('lanes', { lanes: account.lanes.join(', ') })] : []),
    account.engine,
    ...(account.quota !== null
      ? [t('quota-read-at', { time: intl.formatTime(account.quota.readAt, { timeStyle: 'short' }) })]
      : []),
  ];

  return <span className="whitespace-nowrap text-sm text-text-tertiary">{parts.join(' · ')}</span>;
}
